import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { PublicAsset, PublicItem } from '@im-marina/shared';
import { fallbackSrc, toSrcSet } from '../../lib/media';
import { stepIndex } from '../../lib/lightbox-state';
import styles from './Lightbox.module.css';

type Props = { item: PublicItem; index: number };

const SWIPE_THRESHOLD = 50;

export function Lightbox({ item, index }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showInfo, setShowInfo] = useState(false);
  const count = item.media.length;
  const media = item.media[index]!;

  const close = useCallback(() => {
    // Opened from the grid: going back restores the previous history entry and scroll.
    // Opened from a shared link: there is nothing to go back to, so drop the params instead.
    if ((location.state as { fromGrid?: boolean } | null)?.fromGrid) navigate(-1);
    else navigate({ search: '' }, { replace: true, preventScrollReset: true });
  }, [location.state, navigate]);

  // Key presses can arrive faster than the URL update re-renders; track the index we last
  // navigated to so consecutive steps don't start from a stale one.
  const indexRef = useRef(index);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const go = useCallback(
    (delta: number) => {
      if (count < 2) return;
      const next = stepIndex(indexRef.current, delta, count);
      indexRef.current = next;
      navigate(
        { search: `?item=${item.slug}${next ? `&i=${next}` : ''}` },
        { replace: true, state: location.state, preventScrollReset: true },
      );
    },
    [count, item.slug, location.state, navigate],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      // Closing (rather than just unmounting) returns focus to the card that opened it.
      dialog.close();
      root.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  // Warm the cache for neighbouring images so stepping through is instant.
  useEffect(() => {
    for (const d of [1, -1]) {
      const neighbour = item.media[stepIndex(index, d, count)];
      if (neighbour?.kind === 'image' && neighbour.srcset) {
        const img = new Image();
        img.sizes = '100vw';
        img.srcset = toSrcSet(neighbour.srcset) ?? '';
      }
    }
  }, [item, index, count]);

  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== 'mouse' && !(e.target instanceof HTMLVideoElement)) {
      swipeStart.current = { x: e.clientX, y: e.clientY };
    }
  };
  const onPointerUp = (e: PointerEvent) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
  };

  const meta = [item.year, item.medium].filter(Boolean).join(' · ');

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-label={item.title}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
    >
      <title>{`${item.title} — Marina`}</title>

      <button type="button" className={styles.close} onClick={close} autoFocus>
        <span className="visually-hidden">Close</span>
        <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden>
          <path d="M3 3l14 14M17 3L3 17" stroke="currentColor" strokeWidth="1.25" />
        </svg>
      </button>

      <div
        className={styles.stage}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (swipeStart.current = null)}
        onClick={(e) => {
          // Clicking the empty space around the media closes, like most lightboxes.
          if (e.target === e.currentTarget) close();
        }}
      >
        <figure key={media.id + index} className={styles.figure}>
          <MediaView asset={media} />
        </figure>

        {count > 1 && (
          <>
            <button type="button" className={styles.prev} onClick={() => go(-1)}>
              <span className="visually-hidden">Previous</span>
              <Chevron dir="left" />
            </button>
            <button type="button" className={styles.next} onClick={() => go(1)}>
              <span className="visually-hidden">Next</span>
              <Chevron dir="right" />
            </button>
          </>
        )}
      </div>

      <div className={styles.info}>
        <div className={styles.text}>
          <h2 className={styles.title}>{item.title}</h2>
          {meta && <p className={styles.meta}>{meta}</p>}
          {media.caption && <p className={styles.caption}>{media.caption}</p>}
        </div>
        <div className={styles.controls}>
          {item.description && (
            <button
              type="button"
              className={styles.infoToggle}
              aria-expanded={showInfo}
              onClick={() => setShowInfo((v) => !v)}
            >
              {showInfo ? 'Less' : 'About'}
            </button>
          )}
          {count > 1 && (
            <span className={styles.counter} aria-live="polite">
              {index + 1} / {count}
            </span>
          )}
        </div>
        {showInfo && item.description && <p className={styles.description}>{item.description}</p>}
      </div>
    </dialog>
  );
}

function MediaView({ asset }: { asset: PublicAsset }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Opening the lightbox was a user gesture, so playback with sound usually works;
    // fall back to muted autoplay where the browser refuses.
    v.play().catch(() => {
      v.muted = true;
      v.play().catch(() => {});
    });
  }, []);

  if (asset.kind === 'video' && asset.video) {
    return (
      <video
        ref={videoRef}
        className={styles.media}
        src={asset.video}
        poster={asset.poster}
        width={asset.width}
        height={asset.height}
        controls
        playsInline
        preload="metadata"
      />
    );
  }

  return (
    <img
      className={styles.media}
      src={fallbackSrc(asset)}
      srcSet={toSrcSet(asset.srcset)}
      sizes="100vw"
      width={asset.width}
      height={asset.height}
      alt={asset.alt}
    />
  );
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden>
      <path
        d={dir === 'left' ? 'M15 4l-8 8 8 8' : 'M9 4l8 8-8 8'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      />
    </svg>
  );
}
